/**
 * Daten fuer den eigenstaendigen Parking-Spielmodus.
 *
 * Alle Koordinaten sind relativ zum konfigurierbaren Ursprung des
 * `ParkingSystem`. Die Fahrzeugfront zeigt bei `yaw = 0` in +Z-Richtung.
 */

export type ParkingManeuver = 'forward' | 'reverse' | 'parallel';

export interface ParkingTargetDefinition {
  readonly x: number;
  readonly z: number;
  /** Drehung um die Y-Achse; 0 bedeutet Fahrzeugfront in +Z. */
  readonly yaw: number;
  /** Innenbreite der markierten Parkflaeche in Metern. */
  readonly width: number;
  /** Innenlaenge der markierten Parkflaeche in Metern. */
  readonly length: number;
}

export interface ParkingGuidePoint {
  readonly x: number;
  readonly z: number;
}

export interface ParkingTaskDefinition {
  readonly id: string;
  readonly title: string;
  readonly instruction: string;
  readonly maneuver: ParkingManeuver;
  readonly target: ParkingTargetDefinition;
  /**
   * Empfohlene Fahrspur relativ zum Parkplatz-Ursprung. Der letzte Punkt
   * liegt in der Parkluecke; auf der Strecke werden gruene Bodenpfeile
   * angezeigt. So ist die Aufgabe auch ohne Lesen nachvollziehbar.
   */
  readonly approachPath: readonly ParkingGuidePoint[];
  /** Zusaetzlicher Spielraum an jeder Markierungskante. */
  readonly boundaryToleranceM: number;
  readonly angleToleranceDeg: number;
  readonly stopSpeedKmh: number;
  readonly maxAngularSpeedRad: number;
  /** So lange muessen Position, Ausrichtung und Stillstand gleichzeitig stimmen. */
  readonly holdSeconds: number;
  readonly rewardCoins: number;
}

export type ParkedVehicleStyle = 'hatchback' | 'sedan' | 'suv' | 'van';

export interface ParkedVehicleDefinition {
  readonly id: string;
  readonly x: number;
  readonly z: number;
  readonly yaw: number;
  readonly width: number;
  readonly length: number;
  readonly height: number;
  readonly color: number;
  readonly style: ParkedVehicleStyle;
}

export interface ParkingCourseBounds {
  readonly minX: number;
  readonly maxX: number;
  readonly minZ: number;
  readonly maxZ: number;
}

export interface ParkingBarrierDefinition {
  readonly id: string;
  readonly x: number;
  readonly z: number;
  readonly width: number;
  readonly length: number;
  readonly height: number;
}

export interface ParkingIslandDefinition {
  readonly id: string;
  readonly x: number;
  readonly z: number;
  readonly width: number;
  readonly length: number;
}

/** Sichere Innenkante des 100 x 82 Meter grossen Parkplatzes. */
export const PARKING_COURSE_BOUNDS: ParkingCourseBounds = {
  minX: -47,
  maxX: 47,
  minZ: -39,
  maxZ: 39,
};

/**
 * Zwei flache Verkehrsinseln machen aus der freien Asphaltflaeche einen
 * lesbaren Uebungsparcours. Sie bleiben weit unter Fensterhoehe und stehen
 * weder vor den Zielbuchten noch auf den empfohlenen Anfahrwegen.
 */
export const DEFAULT_PARKING_ISLANDS = [
  { id: 'insel-mitte', x: 0, z: 0, width: 10, length: 8 },
  { id: 'insel-ost', x: 29, z: 3, width: 7, length: 26 },
] as const satisfies readonly ParkingIslandDefinition[];

/**
 * Niedrige, breite Schutzkanten statt hoher Balken. Die aeussere Kante hat
 * am Nordrand eine gut sichtbare Einfahrt; die Inseln verhindern Abkuerzer.
 */
export const DEFAULT_PARKING_BARRIERS = [
  { id: 'rand-sued', x: 0, z: -39.5, width: 95, length: 0.8, height: 0.68 },
  { id: 'rand-west', x: -47.5, z: 0, width: 0.8, length: 79, height: 0.68 },
  { id: 'rand-ost', x: 47.5, z: 0, width: 0.8, length: 79, height: 0.68 },
  { id: 'rand-nord-west', x: -27, z: 39.5, width: 40, length: 0.8, height: 0.68 },
  { id: 'rand-nord-ost', x: 27, z: 39.5, width: 40, length: 0.8, height: 0.68 },
  { id: 'insel-mitte-nord', x: 0, z: 4, width: 10.7, length: 0.7, height: 0.52 },
  { id: 'insel-mitte-sued', x: 0, z: -4, width: 10.7, length: 0.7, height: 0.52 },
  { id: 'insel-mitte-west', x: -5, z: 0, width: 0.7, length: 8, height: 0.52 },
  { id: 'insel-mitte-ost', x: 5, z: 0, width: 0.7, length: 8, height: 0.52 },
  { id: 'insel-ost-nord', x: 29, z: 16, width: 7.7, length: 0.7, height: 0.52 },
  { id: 'insel-ost-sued', x: 29, z: -10, width: 7.7, length: 0.7, height: 0.52 },
  { id: 'insel-ost-west', x: 25.5, z: 3, width: 0.7, length: 26, height: 0.52 },
  { id: 'insel-ost-ost', x: 32.5, z: 3, width: 0.7, length: 26, height: 0.52 },
] as const satisfies readonly ParkingBarrierDefinition[];

/**
 * Fuenf abwechslungsreiche Aufgaben: vorwaerts, rueckwaerts und parallel.
 * Die Zielplaetze bleiben in der Standardbelegung bewusst frei.
 */
export const DEFAULT_PARKING_TASKS = [
  {
    id: 'grundkurs-vorwaerts',
    title: 'Vorwärts einparken',
    instruction: 'Vorwärts: Nase zum VORNE-Schild.',
    maneuver: 'forward',
    target: { x: -7, z: -27, yaw: 0, width: 3.25, length: 6.1 },
    approachPath: [
      { x: 0, z: 15 }, { x: -38, z: 15 }, { x: -38, z: -35 },
      { x: -7, z: -35 }, { x: -7, z: -27 },
    ],
    boundaryToleranceM: 0.16,
    angleToleranceDeg: 13,
    stopSpeedKmh: 1.2,
    maxAngularSpeedRad: 0.22,
    holdSeconds: 1.2,
    rewardCoins: 15,
  },
  {
    id: 'grundkurs-rueckwaerts',
    title: 'Rückwärts einparken',
    instruction: 'Rückwärts: Heck zuerst durch EINFAHRT.',
    maneuver: 'reverse',
    target: { x: 10.5, z: -27, yaw: Math.PI, width: 3.25, length: 6.1 },
    approachPath: [
      { x: -7, z: -27 }, { x: -7, z: -35 },
      { x: 10.5, z: -35 }, { x: 10.5, z: -27 },
    ],
    boundaryToleranceM: 0.16,
    angleToleranceDeg: 12,
    stopSpeedKmh: 1.1,
    maxAngularSpeedRad: 0.2,
    holdSeconds: 1.35,
    rewardCoins: 20,
  },
  {
    id: 'parallel-am-bordstein',
    title: 'Seitlich einparken',
    instruction: 'Pfeilen folgen, dann rückwärts seitlich hinein.',
    maneuver: 'parallel',
    target: { x: -24, z: 2, yaw: Math.PI / 2, width: 2.85, length: 7.2 },
    approachPath: [
      { x: 10.5, z: -27 }, { x: 37, z: -34 }, { x: 37, z: 21 },
      { x: -18, z: 21 }, { x: -18, z: -2 }, { x: -24, z: 2 },
    ],
    boundaryToleranceM: 0.18,
    angleToleranceDeg: 11,
    stopSpeedKmh: 1,
    maxAngularSpeedRad: 0.18,
    holdSeconds: 1.45,
    rewardCoins: 30,
  },
  {
    id: 'enge-luecke',
    title: 'Enge Parklücke',
    instruction: 'Rückwärts: Heck zuerst durch EINFAHRT.',
    maneuver: 'reverse',
    target: { x: 17.5, z: 27, yaw: Math.PI, width: 3.15, length: 5.9 },
    approachPath: [
      { x: -24, z: 2 }, { x: -24, z: 17 },
      { x: 17.5, z: 19 }, { x: 17.5, z: 27 },
    ],
    boundaryToleranceM: 0.14,
    angleToleranceDeg: 10,
    stopSpeedKmh: 0.9,
    maxAngularSpeedRad: 0.17,
    holdSeconds: 1.55,
    rewardCoins: 35,
  },
  {
    id: 'abschlussplatz',
    title: 'Parkplatz-Profi',
    instruction: 'Vorwärts: Nase zum VORNE-Schild.',
    maneuver: 'forward',
    target: { x: -7, z: 27, yaw: 0, width: 3.1, length: 5.8 },
    approachPath: [
      { x: 17.5, z: 27 }, { x: 17.5, z: 18 },
      { x: -7, z: 18 }, { x: -7, z: 27 },
    ],
    boundaryToleranceM: 0.13,
    angleToleranceDeg: 9,
    stopSpeedKmh: 0.8,
    maxAngularSpeedRad: 0.15,
    holdSeconds: 1.65,
    rewardCoins: 45,
  },
] as const satisfies readonly ParkingTaskDefinition[];

/**
 * Statische Verkehrsfahrzeuge fuer die Standard-Parkflaeche. Die leicht
 * unterschiedlichen Abmessungen, Formen und Lacke vermeiden eine sterile
 * Kulisse, bleiben dank Instancing aber sehr guenstig zu rendern.
 */
export const DEFAULT_PARKED_VEHICLES = [
  { id: 'parked-01', x: -28, z: -27, yaw: 0.03, width: 1.82, length: 4.18, height: 1.48, color: 0x315f9f, style: 'hatchback' },
  { id: 'parked-02', x: -24.5, z: -27, yaw: -0.02, width: 1.87, length: 4.62, height: 1.46, color: 0xd5d7d9, style: 'sedan' },
  { id: 'parked-03', x: -21, z: -27, yaw: 0.01, width: 1.95, length: 4.56, height: 1.69, color: 0x25282d, style: 'suv' },
  { id: 'parked-04', x: -17.5, z: -27, yaw: -0.025, width: 1.82, length: 4.24, height: 1.5, color: 0xb3262f, style: 'hatchback' },
  { id: 'parked-05', x: -10.5, z: -27, yaw: 0.015, width: 1.9, length: 4.7, height: 1.47, color: 0x826849, style: 'sedan' },
  { id: 'parked-06', x: -3.5, z: -27, yaw: -0.01, width: 1.85, length: 4.35, height: 1.5, color: 0xe7e1d4, style: 'hatchback' },
  { id: 'parked-07', x: 7, z: -27, yaw: Math.PI + 0.02, width: 1.94, length: 4.74, height: 1.72, color: 0x334b36, style: 'suv' },
  { id: 'parked-08', x: 14, z: -27, yaw: Math.PI - 0.015, width: 1.86, length: 4.68, height: 1.46, color: 0x8a9198, style: 'sedan' },
  { id: 'parked-09', x: 17.5, z: -27, yaw: Math.PI + 0.01, width: 1.82, length: 4.22, height: 1.49, color: 0xd49221, style: 'hatchback' },
  { id: 'parked-10', x: 21, z: -27, yaw: Math.PI - 0.025, width: 2.02, length: 5.04, height: 2.05, color: 0xf1f0e8, style: 'van' },
  { id: 'parked-11', x: 24.5, z: -27, yaw: Math.PI, width: 1.96, length: 4.62, height: 1.7, color: 0x712d4b, style: 'suv' },
  { id: 'parked-12', x: -28, z: 27, yaw: Math.PI - 0.02, width: 1.91, length: 4.72, height: 1.47, color: 0x242a34, style: 'sedan' },
  { id: 'parked-13', x: -24.5, z: 27, yaw: Math.PI + 0.025, width: 1.83, length: 4.22, height: 1.49, color: 0xc3c7cc, style: 'hatchback' },
  { id: 'parked-14', x: -21, z: 27, yaw: Math.PI - 0.01, width: 1.97, length: 4.7, height: 1.74, color: 0x294f73, style: 'suv' },
  { id: 'parked-15', x: -17.5, z: 27, yaw: Math.PI + 0.02, width: 1.88, length: 4.76, height: 1.48, color: 0x9a2e22, style: 'sedan' },
  { id: 'parked-16', x: -10.5, z: 27, yaw: 0.015, width: 1.84, length: 4.26, height: 1.5, color: 0x477d55, style: 'hatchback' },
  { id: 'parked-17', x: -3.5, z: 27, yaw: -0.02, width: 1.93, length: 4.66, height: 1.69, color: 0xe0d8c6, style: 'suv' },
  { id: 'parked-18', x: 3.5, z: 27, yaw: 0.01, width: 1.86, length: 4.67, height: 1.46, color: 0x697887, style: 'sedan' },
  { id: 'parked-19', x: 10.5, z: 27, yaw: Math.PI - 0.015, width: 2.01, length: 5.02, height: 2.02, color: 0xeee7d6, style: 'van' },
  { id: 'parked-20', x: 14, z: 27, yaw: Math.PI + 0.02, width: 1.84, length: 4.27, height: 1.51, color: 0xc78219, style: 'hatchback' },
  { id: 'parked-21', x: 21, z: 27, yaw: Math.PI - 0.01, width: 1.95, length: 4.74, height: 1.72, color: 0x44474d, style: 'suv' },
  { id: 'parked-22', x: -33, z: 2, yaw: Math.PI / 2 + 0.015, width: 1.88, length: 4.74, height: 1.47, color: 0xbfc6cf, style: 'sedan' },
  { id: 'parked-23', x: -15, z: 2, yaw: Math.PI / 2 - 0.02, width: 1.83, length: 4.25, height: 1.5, color: 0x7c2834, style: 'hatchback' },
] as const satisfies readonly ParkedVehicleDefinition[];
